<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.log_list.log_list') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <logs-filter
      v-model="isFilterShown"
      :filter="filter"
      @applyFilter="applyFilter"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.actions="{ item }">
        <v-hover v-slot="{ hover }">
          <v-icon
            class="mr-4"
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            @click="showDetailInfoModal(item)"
          >
            mdi-information-outline
          </v-icon>
        </v-hover>
      </template>
    </v-data-table>
    <detail-info-modal
      v-model="isDetailInfoModalShown"
      :detail-info="detailInfo"
    />
  </div>
</template>

<script>
import DetailInfoModal from '@/components/logs/DetailInfoModal';
import LogsFilter from '@/components/logs/LogsFilter';
import { mongoListRegex } from '@/utils/mongo-regex';

export default {
  name: 'LogList',

  components: {
    DetailInfoModal,
    LogsFilter,
  },

  data() {
    return {
      isDetailInfoModalShown: false,
      detailInfo: null,

      total: 0,
      items: [],
      loading: false,
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
        page: 1,
      },

      headers: [
        {
          text: this.$t('common.date'),
          value: 'createdAt',
        },
        {
          text: this.$t('views.log_list.severity'),
          value: 'severity',
        },
        {
          text: this.$t('common.name'),
          value: 'name',
        },
        {
          text: this.$t('views.log_list.service'),
          value: 'ctx.service.name',
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          sortable: false,
          width: 150,
          align: 'center',
        },
      ],

      filter: {
        createdAt: '',
        name: '',
        service: '',
        path: '',
        severity: '',
      },
      isFilterShown: false,
    };
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  methods: {
    async getItems() {
      this.loading = true;

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;

      const params = {};
      params.query = this.mapFilter();
      params.pageSize = itemsPerPage;
      params.page = page;
      if (sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      const { data } = await this.$http.get('log-event', { params }).finally(() => {
        this.loading = false;
      });

      this.items = data.rows.map((it) => ({
        ...it,
        createdAt: it.createdAt && this.$moment(it.createdAt).format('L LT'),
      }));
      this.total = data.total;
    },

    showDetailInfoModal(detailInfo) {
      this.isDetailInfoModalShown = true;
      this.detailInfo = detailInfo;
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.options.page = 1;
      this.getItems();
    },

    mapFilter() {
      const filter = {};
      if (this.filter.createdAt) {
        const [start, end] = this.filter.createdAt.split(' — ');
        filter.createdAt = {
          $gte: this.$moment(start, 'L'),
          $lte: this.$moment(end, 'L').endOf('day'),
        };
      }
      if (this.filter.name) {
        filter.name = mongoListRegex(this.filter.name, 'i');
      }

      if (this.filter.service) {
        filter['ctx.service.name'] = mongoListRegex(this.filter.service, 'i');
      }

      if (this.filter.severity) {
        filter.severity = this.filter.severity;
      }
      let pathQuery = {};

      if (this.filter.path) {
        try {
          pathQuery = JSON.parse(this.filter.path);
        } catch (e) {
          pathQuery = {};
        }
      }

      return Object.assign(filter, pathQuery);
    },
  },
};
</script>
